import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Globe, Plus, X } from "lucide-react";
import { api, type Site } from "../lib/api";
import { TZ_CHOICES, tzOffsetMinutes } from "../lib/format";
import { AppShell } from "../components/AppShell";
import { ErrorBox, PageSpinner, Spinner } from "../components/ui";

export default function SitesPage() {
  const [showForm, setShowForm] = useState(false);
  const sites = useQuery({
    queryKey: ["sites"],
    queryFn: () => api.get<{ sites: Site[] }>("/api/sites"),
  });

  return (
    <AppShell
      right={
        <button type="button" className="btn-primary" onClick={() => setShowForm((value) => !value)}>
          {showForm ? <X size={15} /> : <Plus size={15} />}
          {showForm ? "Batal" : "Situs Baru"}
        </button>
      }
    >
      <div className="space-y-4">
        {showForm && <CreateSiteForm onDone={() => setShowForm(false)} />}

        {sites.isLoading ? (
          <PageSpinner />
        ) : sites.error ? (
          <ErrorBox message="Gagal memuat daftar situs." />
        ) : (sites.data?.sites.length ?? 0) === 0 ? (
          <div className="card p-10 text-center">
            <Globe className="mx-auto mb-3 text-slate-300" size={32} />
            <h2 className="text-sm font-semibold text-slate-700">Belum ada situs</h2>
            <p className="mt-1 text-sm text-slate-500">Tambahkan situs pertama untuk mulai mengumpulkan statistik.</p>
          </div>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {sites.data?.sites.map((site) => (
              <Link key={site.id} to={`/s/${site.id}`} className="card p-4 transition hover:border-emerald-300 hover:shadow">
                <h2 className="truncate text-sm font-semibold text-slate-800">{site.name}</h2>
                <p className="mt-0.5 truncate text-xs text-slate-500">{site.domain ?? "Domain belum diatur"}</p>
                <p className="mt-3 text-[11px] text-slate-400">
                  {site.tz} (UTC{site.tz_offset >= 0 ? "+" : ""}
                  {site.tz_offset / 60}) &middot; dibuat{" "}
                  {new Intl.DateTimeFormat("id-ID", { dateStyle: "medium" }).format(new Date(site.created_at))}
                </p>
              </Link>
            ))}
          </div>
        )}
      </div>
    </AppShell>
  );
}

function CreateSiteForm({ onDone }: { onDone: () => void }) {
  const queryClient = useQueryClient();
  const [name, setName] = useState("");
  const [domain, setDomain] = useState("");
  const [tz, setTz] = useState("Asia/Jakarta");

  const create = useMutation({
    mutationFn: () =>
      api.post("/api/sites", {
        name,
        domain: domain || undefined,
        tz,
        tz_offset: tzOffsetMinutes(tz),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sites"] });
      onDone();
    },
  });

  return (
    <form
      className="card space-y-4 p-4"
      onSubmit={(event) => {
        event.preventDefault();
        create.mutate();
      }}
    >
      <h2 className="text-sm font-semibold text-slate-800">Situs baru</h2>
      <div className="grid gap-4 sm:grid-cols-3">
        <div>
          <label className="label" htmlFor="site-name">
            Nama
          </label>
          <input
            id="site-name"
            className="input"
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="Blog saya"
            required
          />
        </div>
        <div>
          <label className="label" htmlFor="site-domain">
            Domain (opsional)
          </label>
          <input
            id="site-domain"
            className="input"
            value={domain}
            onChange={(event) => setDomain(event.target.value)}
            placeholder="contoh.com"
          />
        </div>
        <div>
          <label className="label" htmlFor="site-tz">
            Zona waktu
          </label>
          <select id="site-tz" className="input" value={tz} onChange={(event) => setTz(event.target.value)}>
            {TZ_CHOICES.map((zone) => (
              <option key={zone} value={zone}>
                {zone}
              </option>
            ))}
          </select>
        </div>
      </div>
      {(create.error as Error | null) && <p className="text-sm text-red-600">{(create.error as Error).message}</p>}
      <div className="flex justify-end gap-2">
        <button type="button" className="btn" onClick={onDone}>
          Batal
        </button>
        <button type="submit" className="btn-primary" disabled={create.isPending}>
          {create.isPending && <Spinner className="text-white" />}
          Simpan
        </button>
      </div>
    </form>
  );
}
